import JSZip from "jszip";
import { CLASHFINDER_COLOR_FIELDS, ColorFieldDef, HOME_COLOR_FIELDS, ImageFieldKey, LINEUP_COLOR_FIELDS } from "./schema";
import { resolveColor } from "./colorResolver";

const BASE_KEYS = [
  "primaryColor",
  "primaryColorDark",
  "primaryColorDarker",
  "primaryColorText",
  "alternativeTextColor",
];

// Base theme colors are always required (the app has no fallback for them),
// everything else is only written out if the user actually overrode it -
// matching the sparse shape of the real *_colors.json files.
const buildConfigJson = (fields: ColorFieldDef[], overrides: Record<string, string>) => {
  const json: Record<string, string> = {};
  fields.forEach((field) => {
    if (field.derivedFrom) return;
    if (BASE_KEYS.includes(field.key) || overrides[field.key]) {
      json[field.key] = resolveColor(fields, overrides, field.key);
    }
  });
  return json;
};

interface IExportThemeArgs {
  homeOverrides: Record<string, string>;
  lineupOverrides: Record<string, string>;
  clashfinderOverrides: Record<string, string>;
  images: Partial<Record<ImageFieldKey, { file: File; url: string }>>;
}

export const exportTheme = async ({
  homeOverrides,
  lineupOverrides,
  clashfinderOverrides,
  images,
}: IExportThemeArgs) => {
  const zip = new JSZip();

  zip.file("colors.json", JSON.stringify(buildConfigJson(HOME_COLOR_FIELDS, homeOverrides), null, 2));
  zip.file(
    "lineup_colors.json",
    JSON.stringify(buildConfigJson(LINEUP_COLOR_FIELDS, lineupOverrides), null, 2)
  );
  zip.file(
    "clashfinder_colors.json",
    JSON.stringify(buildConfigJson(CLASHFINDER_COLOR_FIELDS, clashfinderOverrides), null, 2)
  );

  const imagesFolder = zip.folder("images");
  Object.entries(images).forEach(([key, entry]) => {
    if (!entry) return;
    const extension = entry.file.name.split(".").pop()?.toLowerCase() || "png";
    imagesFolder?.file(`${key}.${extension}`, entry.file);
  });

  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "theme-export.zip";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
